import React, { useState } from 'react';
import axios from 'axios';
import { getCurrencyCode, getCurrencySymbol } from '../utils/formatters';
import HomeIcon from './HomeIcon';

const UPLOAD_URL = '/api/uploads/';

/* ── Icon ── */
const Icon = ({ d, size = 16, sw = 1.75, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ic = {
    back: 'M19 12H5M12 5l-7 7 7 7',
    save: ['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z', 'M17 21v-8H7v8', 'M7 3v5h8'],
    receipt: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    link: ['M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'],
    note: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    upload: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
    dollar: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    tag: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    check: 'M20 6 9 17l-5-5',
    info: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 16v-4', 'M12 8h.01'],
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
};

/* ── Input style ── */
const inp = {
    width: '100%', padding: '10px 13px', border: '1.5px solid #d1fae5',
    borderRadius: 9, fontSize: 14, color: '#1e293b', background: '#fff',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color .2s, box-shadow .2s',
};

function FocusInput({ type = 'text', ...props }) {
    const [f, setF] = useState(false);
    return <input type={type} {...props}
        style={{ ...inp, borderColor: f ? '#10b981' : '#d1fae5', boxShadow: f ? '0 0 0 3px rgba(16,185,129,.12)' : 'none', ...props.style }}
        onFocus={() => setF(true)} onBlur={() => setF(false)} />;
}
function FocusSelect({ children, ...props }) {
    const [f, setF] = useState(false);
    return <select {...props}
        style={{ ...inp, height: 42, borderColor: f ? '#10b981' : '#d1fae5', boxShadow: f ? '0 0 0 3px rgba(16,185,129,.12)' : 'none', cursor: 'pointer', ...props.style }}
        onFocus={() => setF(true)} onBlur={() => setF(false)}>{children}</select>;
}

function Field({ label, required, hint, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', gap: 5, alignItems: 'center' }}>
                {label}{required && <span style={{ color: '#ef4444' }}>*</span>}
                {hint && <span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>— {hint}</span>}
            </label>
            {children}
        </div>
    );
}

/* ── Section card with teal/emerald theme ── */
function Section({ num, icon, title, subtitle, children, action }) {
    return (
        <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #d1fae5', boxShadow: '0 2px 14px rgba(16,185,129,.07)', overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '18px 26px', borderBottom: '1px solid #f0fdf4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(16,185,129,.3)' }}>
                        <Icon d={icon} size={17} color="#fff" sw={2} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '2px 8px', borderRadius: 20, letterSpacing: '0.05em' }}>STEP {num}</span>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{title}</h3>
                        </div>
                        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>{subtitle}</p>}
                    </div>
                </div>
                {action}
            </div>
            <div style={{ padding: '24px 26px' }}>{children}</div>
        </div>
    );
}

/* ── Linked entity pill toggle ── */
function EntityToggle({ label, icon, active, onChange }) {
    return (
        <button type="button" onClick={() => onChange(!active)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${active ? '#10b981' : '#e2e8f0'}`, background: active ? 'linear-gradient(135deg,#d1fae5,#a7f3d0)' : '#f8fafc', color: active ? '#065f46' : '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .18s', boxShadow: active ? '0 2px 8px rgba(16,185,129,.2)' : 'none' }}>
            <Icon d={ic[icon]} size={14} color={active ? '#059669' : '#94a3b8'} />
            {label}
            {active && <Icon d={ic.check} size={12} color="#059669" />}
        </button>
    );
}

export default function CreateExpensePage({ editItem, operatorDetails, masterData, onBack, onSaved, isAdmin }) {
    const { categories = [], paymentMethods = [], vendors = [], vehicles = [], departments = [], drivers = [], clients = [], operators = [], operatorId, country } = masterData;
    const currencyCode = getCurrencyCode(operatorDetails?.country);
    const currencySymbol = getCurrencySymbol(operatorDetails?.country);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [dragOver, setDragOver] = useState(false);

    const blank = {
        amount: '', category: '', paymentMethod: '',
        vehicleId: '', vendorId: '', clientId: '', driverId: '', department: '',
        notes: '', date: new Date().toISOString().split('T')[0],
        isVehicle: false, isVendor: false, isClient: false, isDriver: false, isDepartment: false
    };

    const [fd, setFd] = useState(() => {
        if (!editItem) return blank;
        return {
            amount: editItem.amount,
            category: editItem.category,
            paymentMethod: editItem.paymentMethod,
            vehicleId: (typeof editItem.vehicleId === 'object' ? editItem.vehicleId?._id : editItem.vehicleId) || '',
            vendorId: (typeof editItem.vendorId === 'object' ? editItem.vendorId?._id : editItem.vendorId) || '',
            clientId: (typeof editItem.clientId === 'object' ? editItem.clientId?._id : editItem.clientId) || '',
            driverId: (typeof editItem.driverId === 'object' ? editItem.driverId?._id : editItem.driverId) || '',
            department: editItem.department || '',
            notes: editItem.notes || '',
            date: new Date(editItem.date).toISOString().split('T')[0],
            isVehicle: !!editItem.vehicleId,
            isVendor: !!editItem.vendorId,
            isClient: !!editItem.clientId,
            isDriver: !!editItem.driverId,
            isDepartment: !!editItem.department
        };
    });

    const set = (k, v) => setFd(p => ({ ...p, [k]: v }));

    const handleSave = async (e) => {
        e.preventDefault();
        if (!fd.amount || !fd.category || !fd.paymentMethod) { setError('Please fill in all required fields.'); return; }
        setSaving(true); setError('');
        const data = new FormData();
        data.append('operatorId', isAdmin ? 'admin' : operatorId);
        ['amount', 'category', 'paymentMethod', 'notes', 'date'].forEach(k => data.append(k, fd[k]));
        data.append('vehicleId', fd.isVehicle ? fd.vehicleId : '');
        data.append('vendorId', fd.isVendor ? fd.vendorId : '');
        data.append('clientId', fd.isClient ? fd.clientId : '');
        data.append('driverId', fd.isDriver ? fd.driverId : '');
        data.append('department', fd.isDepartment ? fd.department : '');
        if (attachment) data.append('attachment', attachment);
        try {
            if (editItem) await axios.put(`/api/expenses/${editItem._id}`, data);
            else await axios.post('/api/expenses', data);
            onSaved?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save expense. Please try again.');
        } finally { setSaving(false); }
    };

    const handleDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) setAttachment(file);
    };

    const linkedCount = [fd.isVendor, fd.isClient, fd.isDriver, fd.isVehicle, fd.isDepartment].filter(Boolean).length;

    return (
        <div style={{ background: '#f0fdf4', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>

            {/* ── Sticky header ── */}
            <div style={{ background: '#fff', borderBottom: '1px solid #d1fae5', padding: '0 32px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(16,185,129,.06)' }}>
                {/* Breadcrumb */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', padding: '10px 0 0' }}>
                    <HomeIcon size={14} /><span style={{ color: '#cbd5e1' }}>›</span><span>Finance</span><span style={{ color: '#cbd5e1' }}>›</span>
                    <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 600 }}>Expenses</button>
                    <span style={{ color: '#cbd5e1' }}>›</span>
                    <span style={{ color: '#1e293b', fontWeight: 600 }}>{editItem ? 'Edit Expense' : 'Record Expense'}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <button onClick={onBack}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1.5px solid #d1fae5', borderRadius: 8, color: '#64748b', cursor: 'pointer', padding: '7px 14px', fontSize: 13, fontWeight: 600, transition: 'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#059669'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1fae5'; e.currentTarget.style.color = '#64748b'; }}>
                            <Icon d={ic.back} size={15} /> Back
                        </button>
                        <div style={{ width: 1, height: 28, background: '#d1fae5' }} />
                        <div>
                            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.3px' }}>
                                {editItem ? 'Edit Expense' : 'Record New Expense'}
                            </h1>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>Fill in all 3 sections and save — simple and fast</p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* Mini progress dots */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {[1, 2, 3].map(n => (
                                <React.Fragment key={n}>
                                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(16,185,129,.3)' }}>{n}</div>
                                    {n < 3 && <div style={{ width: 24, height: 2, background: '#a7f3d0', borderRadius: 2 }} />}
                                </React.Fragment>
                            ))}
                        </div>
                        <button onClick={handleSave} disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,.35)', opacity: saving ? 0.7 : 1, transition: 'all .2s' }}>
                            <Icon d={ic.save} size={15} color="#fff" />
                            {saving ? 'Saving…' : editItem ? 'Update Expense' : 'Save Expense'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div style={{ margin: '16px 32px 0', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon d={ic.info} size={16} color="#dc2626" /> {error}
                </div>
            )}

            {/* ── Body: form + summary panel ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>

                {/* LEFT: Form */}
                <form onSubmit={handleSave}>

                    {/* ── STEP 1: Basics ── */}
                    <Section num={1} icon={ic.dollar} title="Expense Basics" subtitle="Core details — date, category, method and amount">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field label="Date" required>
                                <FocusInput type="date" value={fd.date} onChange={e => set('date', e.target.value)} required />
                            </Field>
                            <Field label="Expense Category" required>
                                <FocusSelect value={fd.category} onChange={e => set('category', e.target.value)} required>
                                    <option value="">Select Category</option>
                                    {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                </FocusSelect>
                            </Field>
                            <Field label="Payment Method" required>
                                <FocusSelect value={fd.paymentMethod} onChange={e => set('paymentMethod', e.target.value)} required>
                                    <option value="">Select Method</option>
                                    {paymentMethods.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                </FocusSelect>
                            </Field>
                            <Field label={`Amount (${currencyCode})`} required>
                                <div style={{ position: 'relative' }}>
                                    <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontWeight: 800, fontSize: 14, pointerEvents: 'none', whiteSpace: 'nowrap' }}>{currencySymbol}</span>
                                    <FocusInput type="number" step="0.001" min="0" placeholder="0.00"
                                        value={fd.amount} onChange={e => set('amount', e.target.value)} required
                                        style={{ paddingLeft: Math.max(30, 13 + String(currencySymbol || '').length * 10 + 12) }} />
                                </div>
                            </Field>
                        </div>
                    </Section>

                    {/* ── STEP 2: Linked Entities ── */}
                    <Section num={2} icon={ic.link} title="Link to Entities" subtitle="Optionally link this expense to vendors, clients, drivers etc."
                        action={linkedCount > 0 && (
                            <span style={{ fontSize: 12, background: '#d1fae5', color: '#065f46', fontWeight: 700, padding: '4px 10px', borderRadius: 20 }}>
                                {linkedCount} linked
                            </span>
                        )}>
                        {/* Toggle pills */}
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                            <EntityToggle label="Vendor" icon="user" active={fd.isVendor} onChange={v => set('isVendor', v)} />
                            <EntityToggle label="Client" icon="building" active={fd.isClient} onChange={v => set('isClient', v)} />
                            <EntityToggle label="Driver" icon="user" active={fd.isDriver} onChange={v => set('isDriver', v)} />
                            <EntityToggle label="Vehicle" icon="truck" active={fd.isVehicle} onChange={v => set('isVehicle', v)} />
                            <EntityToggle label="Department" icon="tag" active={fd.isDepartment} onChange={v => set('isDepartment', v)} />
                        </div>

                        {/* Dynamic dropdowns */}
                        {(fd.isVendor || fd.isClient || fd.isDriver || fd.isVehicle || fd.isDepartment) && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16, padding: '18px 20px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #a7f3d0' }}>
                                {fd.isVendor && (
                                    <Field label="Vendor">
                                        <FocusSelect value={fd.vendorId} onChange={e => set('vendorId', e.target.value)} required={fd.isVendor}>
                                            <option value="">Select Vendor</option>
                                            {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                        </FocusSelect>
                                    </Field>
                                )}
                                {fd.isClient && (
                                    <Field label="Client">
                                        <FocusSelect value={fd.clientId} onChange={e => set('clientId', e.target.value)} required={fd.isClient}>
                                            <option value="">Select Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </FocusSelect>
                                    </Field>
                                )}
                                {fd.isDriver && (
                                    <Field label="Driver">
                                        <FocusSelect value={fd.driverId} onChange={e => set('driverId', e.target.value)} required={fd.isDriver}>
                                            <option value="">Select Driver</option>
                                            {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                        </FocusSelect>
                                    </Field>
                                )}
                                {fd.isVehicle && (
                                    <Field label="Vehicle">
                                        <FocusSelect value={fd.vehicleId} onChange={e => set('vehicleId', e.target.value)} required={fd.isVehicle}>
                                            <option value="">Select Vehicle</option>
                                            {vehicles.map(v => <option key={v._id} value={v._id}>{v.plateNumber} ({v.brand} {v.model})</option>)}
                                        </FocusSelect>
                                    </Field>
                                )}
                                {fd.isDepartment && (
                                    <Field label="Department">
                                        <FocusSelect value={fd.department} onChange={e => set('department', e.target.value)} required={fd.isDepartment}>
                                            <option value="">Select Department</option>
                                            {departments.map(d => <option key={d._id} value={d.name}>{d.name}</option>)}
                                        </FocusSelect>
                                    </Field>
                                )}
                            </div>
                        )}
                        {!fd.isVendor && !fd.isClient && !fd.isDriver && !fd.isVehicle && !fd.isDepartment && (
                            <div style={{ textAlign: 'center', padding: '18px', color: '#94a3b8', fontSize: 13, background: '#f8fffe', borderRadius: 10, border: '1.5px dashed #a7f3d0' }}>
                                Toggle any option above to link this expense to a specific entity
                            </div>
                        )}
                    </Section>

                    {/* ── STEP 3: Notes & Receipt ── */}
                    <Section num={3} icon={ic.note} title="Notes & Receipt" subtitle="Add any notes and upload a supporting document">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            <Field label="Notes" hint="optional">
                                <textarea rows={5} placeholder="Add any additional notes or description…"
                                    value={fd.notes} onChange={e => set('notes', e.target.value)}
                                    style={{ ...inp, borderColor: '#d1fae5', resize: 'vertical', lineHeight: 1.6 }}
                                    onFocus={e => { e.target.style.borderColor = '#10b981'; e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,.12)'; }}
                                    onBlur={e => { e.target.style.borderColor = '#d1fae5'; e.target.style.boxShadow = 'none'; }} />
                            </Field>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <Field label="Upload Document / Receipt">
                                    <div
                                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={handleDrop}
                                        onClick={() => document.getElementById('exp-file-input').click()}
                                        style={{ border: `2px dashed ${dragOver ? '#10b981' : '#a7f3d0'}`, borderRadius: 12, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#d1fae5' : '#f0fdf4', transition: 'all .2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: attachment ? 'linear-gradient(135deg,#10b981,#059669)' : '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s' }}>
                                            <Icon d={attachment ? ic.check : ic.upload} size={18} color={attachment ? '#fff' : '#10b981'} />
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: attachment ? '#065f46' : '#374151' }}>
                                            {attachment ? attachment.name : 'Click or drag file here'}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>JPG, PNG, PDF — max 5MB</div>
                                        <input id="exp-file-input" type="file" accept="image/*,application/pdf"
                                            style={{ display: 'none' }} onChange={e => setAttachment(e.target.files[0])} />
                                    </div>
                                </Field>

                                {/* Show existing attachment */}
                                {editItem?.attachment && !attachment && (
                                    <a href={`${UPLOAD_URL}${editItem.attachment}`} target="_blank" rel="noreferrer"
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#059669', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                        <Icon d={ic.eye} size={14} color="#059669" /> View existing receipt
                                    </a>
                                )}
                            </div>
                        </div>
                    </Section>

                    {/* ── Footer ── */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingBottom: 40 }}>
                        <button type="button" onClick={onBack}
                            style={{ background: '#fff', border: '1.5px solid #d1fae5', borderRadius: 10, color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '11px 26px', transition: 'all .15s' }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#10b981'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#d1fae5'}>Cancel</button>
                        <button type="submit" disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 28px', fontSize: 14, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,.35)', opacity: saving ? 0.7 : 1 }}>
                            <Icon d={ic.save} size={15} color="#fff" />
                            {saving ? 'Saving…' : editItem ? 'Update Expense' : 'Save Expense'}
                        </button>
                    </div>
                </form>

                {/* RIGHT: Summary panel */}
                <div>
                    <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #d1fae5', boxShadow: '0 4px 24px rgba(16,185,129,.1)', overflow: 'hidden', position: 'sticky', top: 24 }}>
                        {/* Panel header */}
                        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.05em' }}>EXPENSE SUMMARY</span>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', opacity: 0.7 }} />
                        </div>

                        <div style={{ padding: '20px' }}>
                            {/* Amount highlight */}
                            <div style={{ textAlign: 'center', padding: '20px 16px', background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)', borderRadius: 14, border: '1px solid #a7f3d0', marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Total Amount</div>
                                <div style={{ fontSize: 32, fontWeight: 900, color: '#059669', letterSpacing: '-1px' }}>
                                    {currencySymbol}{fd.amount ? Number(fd.amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'}
                                </div>
                            </div>

                            {/* Detail rows */}
                            {[
                                ['Date', fd.date || '—'],
                                ['Category', fd.category || '—'],
                                ['Method', fd.paymentMethod || '—'],
                            ].map(([lbl, val]) => (
                                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0fdf4', fontSize: 13 }}>
                                    <span style={{ color: '#94a3b8', fontWeight: 500 }}>{lbl}</span>
                                    <span style={{ color: '#1e293b', fontWeight: 700, maxWidth: 140, textAlign: 'right', wordBreak: 'break-word' }}>{val}</span>
                                </div>
                            ))}

                            {/* Linked entities */}
                            {linkedCount > 0 && (
                                <div style={{ marginTop: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Linked To</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {fd.isVendor && <span style={{ fontSize: 11, background: '#eff6ff', color: '#3b82f6', fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Vendor</span>}
                                        {fd.isClient && <span style={{ fontSize: 11, background: '#f0fdf4', color: '#16a34a', fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Client</span>}
                                        {fd.isDriver && <span style={{ fontSize: 11, background: '#fffbeb', color: '#d97706', fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Driver</span>}
                                        {fd.isVehicle && <span style={{ fontSize: 11, background: '#f0f7ff', color: '#7c3aed', fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Vehicle</span>}
                                        {fd.isDepartment && <span style={{ fontSize: 11, background: '#fdf4ff', color: '#a21caf', fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Dept</span>}
                                    </div>
                                </div>
                            )}

                            {/* Receipt indicator */}
                            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: attachment || editItem?.attachment ? '#f0fdf4' : '#f8fafc', borderRadius: 10, border: `1px solid ${attachment || editItem?.attachment ? '#a7f3d0' : '#e2e8f0'}` }}>
                                <Icon d={ic.receipt} size={15} color={attachment || editItem?.attachment ? '#10b981' : '#94a3b8'} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: attachment || editItem?.attachment ? '#065f46' : '#94a3b8' }}>
                                    {attachment ? `Receipt: ${attachment.name.slice(0, 20)}…` : editItem?.attachment ? 'Receipt attached' : 'No receipt yet'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}
